import { useEffect, useRef } from 'react';
import { motion, useScroll, useTransform, useSpring, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { HeroLogo } from '@/components/ui/AnimatedLogo';
import { Star, Play, Calendar, Users, Instagram, ChevronDown, Music, Mic2 } from 'lucide-react';

// Floating Particle Component
const FloatingParticle = ({ delay = 0, duration = 5, x = 0, y = 0 }) => (
    <motion.div
        className="absolute w-2 h-2 rounded-full bg-primary/30"
        style={{ left: `${x}%`, top: `${y}%` }}
        animate={{
            y: [0, -30, 0],
            x: [0, 15, 0],
            scale: [1, 1.5, 1],
            opacity: [0.3, 0.7, 0.3],
        }}
        transition={{
            duration: duration,
            repeat: Infinity,
            delay: delay,
            ease: 'easeInOut',
        }}
    />
);

// Music Note Component
const FloatingMusicNote = ({ delay = 0, x = 0, note = '♪' }) => (
    <motion.div
        className="absolute text-2xl text-primary/20"
        style={{ left: `${x}%`, bottom: '10%' }}
        animate={{
            y: [0, -200],
            x: [0, Math.random() * 50 - 25],
            opacity: [0, 0.6, 0],
            rotate: [0, Math.random() * 60 - 30],
        }}
        transition={{
            duration: 4,
            repeat: Infinity,
            delay: delay,
            ease: 'easeOut',
        }}
    >
        {note}
    </motion.div>
);

// 3D Card Component
const Card3D = ({ children, className = '' }) => {
    const ref = useRef(null);

    const handleMouseMove = (e) => {
        if (!ref.current) return;
        const rect = ref.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;
        const rotateX = (y - centerY) / 20;
        const rotateY = (centerX - x) / 20;
        ref.current.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.02, 1.02, 1.02)`;
    };

    const handleMouseLeave = () => {
        if (!ref.current) return;
        ref.current.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)';
    };

    return (
        <div
            ref={ref}
            className={`transition-transform duration-200 ease-out ${className}`}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            style={{ transformStyle: 'preserve-3d' }}
        >
            {children}
        </div>
    );
};

export const HeroSection = () => {
    const containerRef = useRef(null);
    const isInView = useInView(containerRef, { once: true });
    
    const { scrollYProgress } = useScroll({
        target: containerRef,
        offset: ['start start', 'end start'],
    });

    const y = useTransform(scrollYProgress, [0, 1], ['0%', '50%']);
    const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
    const scale = useTransform(scrollYProgress, [0, 0.5], [1, 0.8]);
    const smoothY = useSpring(y, { stiffness: 50, damping: 20 });
    const smoothOpacity = useSpring(opacity, { stiffness: 50, damping: 20 });
    const smoothScale = useSpring(scale, { stiffness: 50, damping: 20 });

    // Generate particles
    const particles = Array.from({ length: 15 }, (_, i) => ({
        x: Math.random() * 100,
        y: Math.random() * 100,
        delay: Math.random() * 3,
        duration: 4 + Math.random() * 3,
    }));

    // Music notes
    const musicNotes = ['♪', '♫', '♬', '♩', '♪', '♫'];

    return (
        <section ref={containerRef} className="relative min-h-screen flex items-center justify-center overflow-hidden">
            {/* Parallax Background Image */}
            <motion.div 
                className="absolute inset-0 z-0"
                style={{ y: smoothY }}
            >
                <img
                    src="https://customer-assets.emergentagent.com/job_yash-thakur-band/artifacts/zrs4fm4l_6.jpeg"
                    alt="Hawayein The Band Live Performance"
                    className="w-full h-[120%] object-cover object-center"
                />
                {/* Gradient Overlays */}
                <div className="absolute inset-0 bg-gradient-to-b from-background via-background/80 to-background" />
                <div className="absolute inset-0 bg-gradient-to-r from-background/90 via-transparent to-background/70" />
            </motion.div>

            {/* Animated Background Elements */}
            <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
                {/* Spotlight Effects */}
                <motion.div
                    className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-primary/20 rounded-full blur-[150px]"
                    animate={{
                        scale: [1, 1.2, 1],
                        opacity: [0.2, 0.4, 0.2],
                        x: [0, 50, 0],
                    }}
                    transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
                />
                <motion.div
                    className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-accent/15 rounded-full blur-[120px]"
                    animate={{
                        scale: [1, 1.3, 1],
                        opacity: [0.15, 0.3, 0.15],
                        x: [0, -30, 0],
                    }}
                    transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
                />
                <motion.div
                    className="absolute top-1/3 right-1/3 w-[400px] h-[400px] bg-amber/10 rounded-full blur-[100px]"
                    animate={{
                        scale: [1, 1.2, 1],
                        opacity: [0.1, 0.2, 0.1],
                    }}
                    transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
                />

                {/* Floating Particles */}
                {particles.map((particle, i) => (
                    <FloatingParticle key={i} {...particle} />
                ))}

                {/* Floating Music Notes */}
                {musicNotes.map((note, i) => (
                    <FloatingMusicNote key={i} note={note} x={10 + i * 15} delay={i * 0.8} />
                ))}

                {/* Grid Pattern */}
                <div 
                    className="absolute inset-0 opacity-[0.02]"
                    style={{
                        backgroundImage: `
                            linear-gradient(hsl(var(--primary)) 1px, transparent 1px),
                            linear-gradient(90deg, hsl(var(--primary)) 1px, transparent 1px)
                        `,
                        backgroundSize: '100px 100px',
                    }}
                />
            </div>

            {/* Content */}
            <motion.div 
                className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 pt-24 pb-16"
                style={{ opacity: smoothOpacity, scale: smoothScale }}
            >
                <div className="max-w-5xl mx-auto text-center">
                    {/* Pre-title Badge */}
                    <motion.div
                        initial={{ opacity: 0, y: 20, scale: 0.9 }}
                        animate={isInView ? { opacity: 1, y: 0, scale: 1 } : {}}
                        transition={{ duration: 0.6 }}
                    >
                        <Badge 
                            variant="outline" 
                            className="mb-8 px-5 py-2 border-primary/40 bg-primary/10 text-primary font-body text-sm backdrop-blur-sm"
                        >
                            <Star className="w-4 h-4 mr-2 fill-amber text-amber" />
                            India's Premier Sufi & Bollywood Live Band
                        </Badge>
                    </motion.div>

                    {/* Animated Hero Logo */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={isInView ? { opacity: 1, scale: 1 } : {}}
                        transition={{ duration: 0.8, delay: 0.2 }}
                    >
                        <HeroLogo />
                    </motion.div>

                    {/* Tagline */}
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={isInView ? { opacity: 1, y: 0 } : {}}
                        transition={{ duration: 0.6, delay: 0.6 }}
                        className="font-body text-lg sm:text-xl md:text-2xl text-muted-foreground mt-8 mb-10 max-w-2xl mx-auto"
                    >
                        Feel the Music in the Air — Led by <span className="text-primary font-semibold">Yash Singh Thakur</span>, 
                        creating unforgettable Sufi & Bollywood experiences with 2000+ shows
                    </motion.p>

                    {/* CTA Buttons with 3D Effect */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={isInView ? { opacity: 1, y: 0 } : {}}
                        transition={{ duration: 0.6, delay: 0.8 }}
                        className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-14"
                    >
                        <Card3D>
                            <Link to="/book">
                                <Button
                                    size="lg"
                                    className="relative overflow-hidden bg-gradient-to-r from-primary via-accent to-primary bg-[length:200%_100%] text-foreground font-heading font-bold px-10 py-7 rounded-2xl text-lg hover:bg-[position:100%_0] transition-all duration-500 group shadow-glow-lg"
                                >
                                    <motion.span
                                        className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                                        animate={{ x: ['-100%', '100%'] }}
                                        transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
                                    />
                                    <Calendar className="w-5 h-5 mr-2 group-hover:rotate-12 transition-transform" />
                                    Book Us for Your Event
                                </Button>
                            </Link>
                        </Card3D>

                        <Card3D>
                            <Link to="/performances">
                                <Button
                                    size="lg"
                                    variant="outline"
                                    className="border-2 border-primary/50 bg-card/30 backdrop-blur-md text-foreground font-heading font-bold px-10 py-7 rounded-2xl text-lg hover:bg-primary/10 hover:border-primary group"
                                >
                                    <Play className="w-5 h-5 mr-2 group-hover:scale-125 transition-transform" />
                                    Watch Our Performances
                                </Button>
                            </Link>
                        </Card3D>
                    </motion.div>

                    {/* Trust Badges with Animation */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={isInView ? { opacity: 1, y: 0 } : {}}
                        transition={{ duration: 0.6, delay: 1 }}
                        className="flex flex-wrap justify-center items-center gap-4 sm:gap-8"
                    >
                        {/* Rating Badge */}
                        <Card3D>
                            <motion.div 
                                className="flex items-center gap-3 glass px-5 py-3 rounded-2xl backdrop-blur-md border border-border/30"
                                whileHover={{ scale: 1.05 }}
                            >
                                <div className="flex">
                                    {[...Array(5)].map((_, i) => (
                                        <motion.div
                                            key={i}
                                            initial={{ opacity: 0, scale: 0 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            transition={{ delay: 1.2 + i * 0.1 }}
                                        >
                                            <Star className="w-4 h-4 fill-amber text-amber" />
                                        </motion.div>
                                    ))}
                                </div>
                                <span className="font-heading font-bold text-lg text-foreground">4.8</span>
                                <span className="text-muted-foreground text-sm font-body">Rating</span>
                            </motion.div>
                        </Card3D>

                        {/* Events Count */}
                        <Card3D>
                            <motion.div 
                                className="flex items-center gap-3 glass px-5 py-3 rounded-2xl backdrop-blur-md border border-border/30"
                                whileHover={{ scale: 1.05 }}
                            >
                                <Mic2 className="w-5 h-5 text-primary" />
                                <motion.span 
                                    className="font-heading font-bold text-lg text-foreground"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ delay: 1.3 }}
                                >
                                    2000+
                                </motion.span>
                                <span className="text-muted-foreground text-sm font-body">Shows</span>
                            </motion.div>
                        </Card3D>

                        {/* Instagram Followers */}
                        <Card3D>
                            <motion.div 
                                className="flex items-center gap-3 glass px-5 py-3 rounded-2xl backdrop-blur-md border border-border/30"
                                whileHover={{ scale: 1.05 }}
                            >
                                <Instagram className="w-5 h-5 text-accent" />
                                <motion.span 
                                    className="font-heading font-bold text-lg text-foreground"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ delay: 1.4 }}
                                >
                                    4,800+
                                </motion.span>
                                <span className="text-muted-foreground text-sm font-body">Followers</span>
                            </motion.div>
                        </Card3D>
                    </motion.div>
                </div>
            </motion.div>

            {/* Scroll Indicator */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.5, duration: 0.5 }}
                className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
            >
                <motion.div
                    animate={{ y: [0, 10, 0] }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className="flex flex-col items-center gap-2"
                >
                    <span className="text-xs text-muted-foreground font-body tracking-wider">SCROLL</span>
                    <motion.div
                        className="w-6 h-10 rounded-full border-2 border-muted-foreground/40 flex items-start justify-center p-1.5"
                    >
                        <motion.div
                            animate={{ 
                                y: [0, 12, 0],
                                opacity: [1, 0.3, 1] 
                            }}
                            transition={{ duration: 2, repeat: Infinity }}
                            className="w-1.5 h-2.5 bg-primary rounded-full"
                        />
                    </motion.div>
                </motion.div>
            </motion.div>
        </section>
    );
};
